import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const platforms = [
  { name: 'freematch', rate: 0, color: '#7C3AED', highlight: true },
  { name: '크몽', rate: 0.22, color: '#EF4444' },
  { name: '숨고', rate: 0.20, color: '#F59E0B' },
  { name: '탈잉', rate: 0.15, color: '#6B7280' },
  { name: '클래스101', rate: 0.30, color: '#6B7280' },
];

const FeeCalculatorPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [amount, setAmount] = useState(500000);
  const [inputVal, setInputVal] = useState('500,000');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    const num = parseInt(raw || '0', 10);
    setAmount(num);
    setInputVal(num.toLocaleString());
  };

  const fmt = (n: number) => `₩${n.toLocaleString()}`;

  return (
    <div style={{
      minHeight: '100vh', background: '#000',
      fontFamily: "-apple-system,'Pretendard',sans-serif",
      padding: '40px 20px',
    }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 6, letterSpacing: '-0.5px' }}>
          {t('feeCalc.title')}
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', marginBottom: 32 }}>
          {t('feeCalc.subtitle')}
        </p>

        {/* 금액 입력 */}
        <div style={{
          background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 14, padding: '20px 24px', marginBottom: 24,
        }}>
          <label style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 10 }}>
            {t('feeCalc.amountLabel')}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 22, color: '#a78bfa', fontWeight: 700 }}>₩</span>
            <input
              type="text"
              value={inputVal}
              onChange={handleChange}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px',
                fontFamily: 'inherit',
              }}
              placeholder="0"
            />
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }}>{t('tradeModal.wonSuffix')}</span>
          </div>
          {/* 빠른 선택 */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {[100000, 500000, 1000000, 3000000, 5000000].map(v => (
              <button
                key={v}
                onClick={() => { setAmount(v); setInputVal(v.toLocaleString()); }}
                style={{
                  padding: '5px 12px', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 8, background: amount === v ? 'rgba(124,58,237,0.2)' : 'transparent',
                  color: amount === v ? '#a78bfa' : 'rgba(255,255,255,0.4)',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                  transition: 'all 0.15s',
                }}
              >
                {i18n.language === 'ko' ? (v >= 10000 ? `${v / 10000}만원` : `${v.toLocaleString()}원`) : `₩${v.toLocaleString()}`}
              </button>
            ))}
          </div>
        </div>

        {/* 플랫폼 비교 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {platforms.map(p => {
            const fee = Math.round(amount * p.rate);
            const receive = amount - fee;
            const barWidth = p.rate === 0 ? 2 : Math.min(100, Math.round(p.rate * 100 / 0.3 * 100));
            return (
              <div
                key={p.name}
                style={{
                  background: p.highlight ? 'rgba(124,58,237,0.12)' : '#0d0d0d',
                  border: `1px solid ${p.highlight ? 'rgba(124,58,237,0.35)' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: 14, padding: '18px 22px',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{p.name}</span>
                    {p.highlight && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: '#a78bfa',
                        background: 'rgba(124,58,237,0.2)', padding: '2px 8px', borderRadius: 6,
                      }}>{t('feeCalc.currentPlatform')}</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: p.rate === 0 ? '#34d399' : '#f87171',
                  }}>
                    {t('feeCalc.feeRate', { n: (p.rate * 100).toFixed(0) })}
                  </span>
                </div>

                {/* 바 */}
                <div style={{
                  height: 6, background: 'rgba(255,255,255,0.07)',
                  borderRadius: 3, marginBottom: 12, overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', width: `${barWidth}%`,
                    background: p.color, borderRadius: 3,
                    transition: 'width 0.3s',
                  }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>{t('transactions.statFees')} </span>
                    <span style={{ color: p.rate === 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
                      {fmt(fee)}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>{t('feeCalc.receive')} </span>
                    <span style={{ color: '#fff', fontWeight: 700 }}>{fmt(receive)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 절약액 요약 */}
        {amount > 0 && (
          <div style={{
            marginTop: 20, background: 'rgba(52,211,153,0.08)',
            border: '1px solid rgba(52,211,153,0.2)',
            borderRadius: 14, padding: '18px 22px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                {t('feeCalc.savingsVsKmong')}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#34d399', letterSpacing: '-0.5px' }}>
                {fmt(Math.round(amount * 0.22))}
              </div>
            </div>
            <div style={{ fontSize: 36 }}>🎉</div>
          </div>
        )}

        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginTop: 20, textAlign: 'center' }}>
          {t('feeCalc.disclaimer')}
        </p>
      </div>
    </div>
  );
};

export default FeeCalculatorPage;
